import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "store";
import whatsNewService from "api/whatsNew/whatsNew.service";
import { setWhatsNewFeed, setWhatsNewLoaded, setWhatsNewLocale, setWhatsNewTours } from "store/whatsNewSlice";

// Page 1 covers the panel's list (10) plus headroom for resolving a hero-tour item by id from the
// cached feed (tour content lives in the feed, not the tours map).
const BOOT_PAGE_SIZE = 20;

// Fetches the feed (page 1) + the tours map when the user is authorized, and AGAIN whenever the UI
// language changes. Mounted in Layout.
//
// The refetch-on-language is not optional: feed content is localized SERVER-side from the request's
// `Accept-Language` (see `whatsNew.service.ts`), so the cached `items` stay in the old language after
// `i18n.changeLanguage()` — which does not reload the page — until something re-requests them. That
// stale cache feeds the bell panel AND the hero tour modal (`activeTourItem` is resolved out of it).
//
// Guarded on Redux state (not a per-instance ref) so it survives StrictMode's mount→unmount→remount and
// Layout's per-route remounts: one fetch per locale, and — crucially — its dispatches are NEVER
// cancelled on cleanup. An earlier version cancelled the dispatch in the effect teardown, which under
// StrictMode killed the only fetch's result and left the feed/tours empty until a later remount re-ran
// it (the "empty until you visit /whats-new" bug). Dispatching to a global store after unmount is safe,
// so there's nothing to cancel. `startedRef` holds the language of this mount's in-flight fetch, which
// dedupes the synchronous StrictMode double-invoke before the store catches up.
//
// `setWhatsNewLocale` fires in `finally`, on the failure path too — otherwise a boot that 500s would
// leave `locale` null and re-fire the fetch on every Layout remount.
//
// Defensive try/catch: the handover warns the endpoints 500 until the BE runs its prod migration, and a
// boot fetch must not turn that into an unhandled rejection — the app just renders with an empty feed.
const useWhatsNewBoot = () => {
  const dispatch = useAppDispatch();
  const { i18n } = useTranslation();
  const language = i18n.language;
  const isAuthorized = useAppSelector((s) => s.common.isAuthorized);
  const loaded = useAppSelector((s) => s.whatsNew.loaded);
  const cachedLocale = useAppSelector((s) => s.whatsNew.locale);
  const startedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isAuthorized || !language) return;
    if (loaded && cachedLocale === language) return;
    if (startedRef.current === language) return;
    startedRef.current = language;

    (async () => {
      try {
        const [feedRes, toursRes] = await Promise.all([whatsNewService.getFeed({ page: 1, page_size: BOOT_PAGE_SIZE }), whatsNewService.getTours()]);

        // Service returns the response body directly. Guard on the real shape — a handled auth error
        // resolves to a `{ _handled: true }` envelope with no `items`/tour keys, which these skip.
        if (Array.isArray(feedRes?.items)) {
          dispatch(setWhatsNewFeed({ items: feedRes.items, unreadCount: feedRes.unread_count ?? 0 }));
        }
        if (toursRes && !(toursRes as { _handled?: boolean })._handled && typeof toursRes === "object") {
          dispatch(setWhatsNewTours(toursRes));
        }
      } catch {
        /* feed unavailable (pre-migration 500 / network) — leave the previous state in place */
      } finally {
        dispatch(setWhatsNewLocale(language));
        dispatch(setWhatsNewLoaded(true));
      }
    })();
  }, [isAuthorized, loaded, cachedLocale, language, dispatch]);
};

export default useWhatsNewBoot;
